import { Person } from "fhir/r5";
import { endpoints, oids } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAuth } from "../../../utils/useAuth";
import { useAxios } from "../../../utils/useAxios";

export type GetPersonResult = {
  persons: Person[];
  canonical?: Person;
};

/**
 * Escolhe o Person da sessão: o registro canônico da conta
 * (`{oids.cpf}-{cpf}`). Se não existir, usa o primeiro da busca.
 */
export function pickCanonicalPerson(
  persons: Person[],
  username: string,
): Person | undefined {
  const cpf = String(username || "").replace(/\D/g, "");
  if (!cpf) return persons[0];
  const canonicalId = `${oids.cpf}-${cpf}`;
  return persons.find((person) => person.id === canonicalId) ?? persons[0];
}

function extractPersonsFromBundle(data: unknown): Person[] {
  if (!data || typeof data !== "object") return [];
  const resource = data as { resourceType?: string; entry?: Array<{ resource?: Person }> };
  // Defesa: se o servidor ainda devolver o recurso único.
  if (resource.resourceType === "Person") {
    return [data as Person];
  }
  if (resource.resourceType !== "Bundle") return [];
  return (resource.entry ?? [])
    .map((entry) => entry.resource)
    .filter((item): item is Person => item?.resourceType === "Person");
}

/**
 * Busca Person por identifier (CPF). Pode retornar 0..N recursos
 * (conta canônica + registros por organização).
 */
export async function GetPerson({
  identifier,
}: {
  identifier?: string;
}): Promise<GetPersonResult | undefined> {
  const cpf = String(identifier || "").replace(/\D/g, "");
  if (!cpf) {
    return { persons: [] };
  }

  const url = `${endpoints.ADMFHIR}/Person?identifier=${cpf}`;

  const bearerToken = await useAuth.getUserToken();
  const headers = {
    Accept: "*/*",
    Authorization: bearerToken,
    "Content-Type": "application/fhir+json",
  };
  const response = await useAxios
    .get(url, { headers, timeout: 8000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`GetPerson: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetPerson ", error);
      // toast.error(`GetPerson Error: ${error?.response?.status}`);
      return undefined;
      // throw new Error();
    });

  if (!response) return { persons: [] };

  const persons = extractPersonsFromBundle(response);
  return {
    persons,
    canonical: pickCanonicalPerson(persons, cpf),
  };
}
